<script setup lang="ts">
/**
 * SVG-Overlay "So wird produziert" für Vorschauen ohne Canvas (inaktive Kacheln, spätere
 * Design-Fläche). Das Overlay liegt über einem Element, das das volle Format
 * (Endformat + Beschnitt) darstellt; viewBox in mm, Striche in konstanter Pixelbreite.
 */
import { computed } from 'vue'
import { geometryHasGuides, guidePathsForFrame, normalizeGeometry } from '@pfe/design-renderer'

const props = withDefaults(defineProps<{
  geometry: any
  /** Seitenverhältnis (Breite/Höhe) des überlagerten Elements, zur Orientierungserkennung */
  aspect?: number | null
  mode?: 'full' | 'cut-only' | 'off'
  compact?: boolean
  /** Zonen in mm relativ zur Trim-Box (Tasse: Griff/Naht), nur im Design-Editor */
  zones?: Array<{ kind: 'hidden' | 'handle' | 'seam'; xMm: number; yMm: number; wMm: number; hMm: number; label?: string }> | null
}>(), { aspect: null, mode: 'full', compact: false, zones: null })

const uid = `gl${Math.random().toString(36).slice(2, 9)}`

const model = computed(() => {
  const g = normalizeGeometry(props.geometry)
  if (!geometryHasGuides(g) || props.mode === 'off') return null
  const fullW = g.trimWidthMm + 2 * g.bleedMm
  const fullH = g.trimHeightMm + 2 * g.bleedMm
  if (!(fullW > 0) || !(fullH > 0)) return null
  const aspect = Number(props.aspect || 0)
  const swapped = fullW !== fullH && aspect > 0 && (aspect >= 1) !== (fullW >= fullH)
  const w = swapped ? fullH : fullW
  const h = swapped ? fullW : fullH
  const guides = guidePathsForFrame(g, { x: 0, y: 0, w, h }, swapped)
  const zones = (props.zones ?? []).map((z) => ({ ...z, x: z.xMm + g.bleedMm, y: z.yMm + g.bleedMm }))
  return {
    w,
    h,
    zones,
    hatch: g.bleedMm > 0 || g.shape !== 'rect',
    region: `${guides.bleedPath} ${guides.cutPath}`,
    cut: guides.cutPath,
    safe: props.mode === 'full' ? guides.safePath : '',
    patternSize: Math.max(1, Math.min(w, h) / 24),
  }
})
</script>

<template>
  <svg
    v-if="model"
    class="guides-overlay"
    :class="{ 'guides-overlay--compact': compact }"
    :viewBox="`0 0 ${model.w} ${model.h}`"
    preserveAspectRatio="none"
    aria-hidden="true"
  >
    <defs>
      <pattern :id="uid" patternUnits="userSpaceOnUse" :width="model.patternSize" :height="model.patternSize" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" :y2="model.patternSize" stroke="rgba(255,255,255,0.55)" :stroke-width="model.patternSize / 6" />
      </pattern>
    </defs>
    <template v-if="model.hatch">
      <path :d="model.region" fill="rgba(10,14,24,0.28)" fill-rule="evenodd" />
      <path :d="model.region" :fill="`url(#${uid})`" fill-rule="evenodd" />
    </template>
    <path :d="model.cut" fill="none" stroke="rgba(15,23,42,0.55)" stroke-width="3" vector-effect="non-scaling-stroke" />
    <path :d="model.cut" fill="none" stroke="rgba(255,255,255,0.98)" stroke-width="1.5" stroke-dasharray="6 4" vector-effect="non-scaling-stroke" />
    <path v-if="model.safe" :d="model.safe" fill="none" stroke="rgba(255,255,255,0.75)" stroke-width="1" stroke-dasharray="2 3" vector-effect="non-scaling-stroke" />
    <g v-for="(z, i) in model.zones" :key="'z' + i">
      <rect :x="z.x" :y="z.y" :width="z.wMm" :height="z.hMm" :fill="`url(#${uid})`" />
      <rect :x="z.x" :y="z.y" :width="z.wMm" :height="z.hMm" fill="rgba(10,14,24,0.25)" stroke="rgba(255,255,255,0.9)" stroke-width="1" stroke-dasharray="3 2" vector-effect="non-scaling-stroke" />
      <text v-if="z.label && !compact" :x="z.x + z.wMm / 2" :y="z.y + z.hMm / 2" text-anchor="middle" dominant-baseline="middle" :font-size="Math.max(2, Math.min(z.wMm, z.hMm) / 4)" fill="#fff" stroke="rgba(15,23,42,0.6)" stroke-width="0.4" paint-order="stroke">{{ z.label }}</text>
    </g>
  </svg>
</template>

<style scoped>
.guides-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
}
.guides-overlay--compact path[stroke-width="3"] {
  stroke-width: 2;
}
</style>
